import { Box } from "@/Components/v2/atoms/Box";
import { Divider } from "@/Components/v2/atoms/Divider";
import { Typography } from "@/Components/v2/atoms/Typography";
import { Config, LabelType } from "@/hooks/useConfig";
import { FormatCurrencyFunction } from "@/hooks/useConfig/types";
import { ROIBreakdownEnum } from "@/shared/types";

import { BreakdownDataProps } from "./BreakdownChart";
import ToolTipROI from "./BreakdownChartToolTips/ToolTipROI";
import ToolTipSpendDollar from "./BreakdownChartToolTips/ToolTipSpendDollar";
import ToolTipSpendPercentage from "./BreakdownChartToolTips/ToolTipSpendPercentage";

interface ParamAttributes {
  dataIndex: number;
  axisValue: string;
  value: number | string;
  color: string;
  seriesName: string;
}

export interface ChannelData {
  totalRevenue: number;
  totalSpend: number;
}

export interface ChannelTotals {
  [channel: string]: ChannelData;
}

interface BreakdownChartToolTipProps {
  params: ParamAttributes[];
  chartLevel: ROIBreakdownEnum;
  channelTotals: ChannelTotals;
  mappedData: BreakdownDataProps[];
  totalAggregateRevenue: number;
  totalAggregateSpend: number;
  label: LabelType;
  disableToolTip?: boolean;
  formatCurrency: FormatCurrencyFunction;
  config: Config;
}

const BreakdownChartToolTip: React.FC<BreakdownChartToolTipProps> = ({
  params,
  mappedData,
  chartLevel,
  channelTotals,
  totalAggregateRevenue,
  totalAggregateSpend,
  label,
  disableToolTip,
  formatCurrency,
  config: { revenueLabel },
}) => {
  const data = params.map((param) => ({
    seriesName: param.seriesName,
    channel: param.axisValue,
    value: param.value,
    color: param.color,
  }));
  const spend = data.filter((item) => item.seriesName.includes("Spend"));
  const revenue = data.filter((item) => item.seriesName.includes(revenueLabel));

  const totalSpend = spend.reduce((acc, item) => acc + Number(item.value), 0);
  const totalRevenue = revenue.reduce((acc, item) => acc + Number(item.value), 0);

  if (disableToolTip) {
    return <div>Insufficient Sample Size</div>;
  }

  return (
    <Box>
      <Typography variant="body1" fontWeight={600}>
        {params[0]!.axisValue}
      </Typography>
      <Divider sx={{ marginBottom: "0.5rem", marginTop: "0.75rem" }} />

      {chartLevel === ROIBreakdownEnum.SPEND_DOLLARS && (
        <ToolTipSpendDollar
          revenue={revenue}
          spend={spend}
          totalRevenue={totalRevenue}
          totalSpend={totalSpend}
          formatCurrency={formatCurrency}
          revenueLabel={revenueLabel}
        />
      )}

      {chartLevel === ROIBreakdownEnum.SPEND_PERCENTAGE && (
        <ToolTipSpendPercentage
          revenue={revenue}
          spend={spend}
          totalRevenue={totalRevenue}
          totalSpend={totalSpend}
          totalAggregateRevenue={totalAggregateRevenue}
          totalAggregateSpend={totalAggregateSpend}
          revenueLabel={revenueLabel}
        />
      )}

      {chartLevel === ROIBreakdownEnum.ROI && (
        <ToolTipROI
          data={data}
          label={label}
          hoveredChartIndex={params[0]!.dataIndex}
          mappedData={mappedData}
          channelTotals={channelTotals}
        />
      )}
    </Box>
  );
};
export default BreakdownChartToolTip;
